import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Landmark, Search as SearchIcon, ClipboardList, Award, Megaphone, ArrowRight,
  Plus, ExternalLink, Loader2, CheckCircle2, Circle, FileText, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const TABS = [
  { key: "grievances",  label: "Grievances",   icon: ClipboardList },
  { key: "schemes",     label: "Schemes",      icon: Award },
  { key: "certificates",label: "Certificates", icon: FileText },
  { key: "notices",     label: "Public Notices", icon: Megaphone },
];

const CERT_KINDS = [
  { key: "birth", label: "Birth" },
  { key: "domicile", label: "Domicile" },
  { key: "income", label: "Income" },
  { key: "caste", label: "Caste" },
  { key: "residence", label: "Residence" },
  { key: "character", label: "Character" },
  { key: "disability", label: "Disability" },
  { key: "senior", label: "Senior Citizen" },
];

const GRIEV_CATEGORIES = ["electricity", "water", "roads", "sanitation", "safety", "healthcare", "other"];

const STATUS_STYLE = {
  submitted: "bg-stone-100 text-stone-700",
  acknowledged: "bg-sky-100 text-sky-700",
  in_progress: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

export default function Gov() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "grievances";

  useSeo({
    title: "Government Portal · find2near",
    description: "File grievances, browse schemes, request digital certificates and read public notices — all in one place.",
    canonical: "/gov",
  });

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="gov-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2">
          <Landmark size={12} /> Government Portal
        </div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
          Every citizen, <span className="text-[#2D5A27]">every service.</span>
        </h1>
        <p className="text-stone-500 text-sm mt-1 max-w-2xl">
          Raise complaints, browse schemes you qualify for, get digital certificates and read local notices from your ward.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-6" data-testid="gov-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSp({ tab: t.key })}
            className={`inline-flex items-center gap-1.5 rounded-full text-sm font-bold px-4 py-2 transition-colors ${
              tab === t.key ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
            data-testid={`gov-tab-${t.key}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "grievances"   && <GrievancesTab />}
      {tab === "schemes"      && <SchemesTab />}
      {tab === "certificates" && <CertificatesTab />}
      {tab === "notices"      && <NoticesTab />}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Grievances
 * ------------------------------------------------------------------------- */
function GrievancesTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "", category: "electricity", city: "", ward: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/gov/grievances").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => { if (user) refresh(); }, [user]);

  const submit = async () => {
    if (!user) { toast.error("Sign in to file a grievance"); return; }
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject + body required"); return; }
    setBusy(true);
    try {
      await api.post("/gov/grievances", form);
      toast.success("Grievance filed — you'll get a status update soon");
      setForm({ subject: "", body: "", category: "electricity", city: form.city, ward: form.ward });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Filing failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_1fr]" data-testid="gov-grievances-tab">
      {/* Form */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <h2 className="heading text-lg font-black text-stone-900 mb-3">File a grievance</h2>
        <div className="space-y-2">
          <input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Subject — e.g. 'Streetlight broken since Monday'"
            className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
            data-testid="griev-subject"
          />
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Describe the issue, location, when it started…"
            rows={4}
            className="w-full px-4 py-2 rounded-2xl border border-stone-200 text-sm outline-none focus:border-[#2D5A27]"
            data-testid="griev-body"
          />
          <div className="grid grid-cols-3 gap-2">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none"
              data-testid="griev-category"
            >
              {GRIEV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="griev-city" />
            <input value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} placeholder="Ward" className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="griev-ward" />
          </div>
          <button
            onClick={submit}
            disabled={busy}
            className="mt-2 w-full bg-[#2D5A27] hover:bg-emerald-800 text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="griev-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            File grievance
          </button>
        </div>
      </div>

      {/* List */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <h2 className="heading text-lg font-black text-stone-900 mb-3">Your grievances</h2>
        {items.length === 0 ? (
          <div className="text-sm text-stone-400 py-10 text-center">No grievances yet.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((g) => (
              <li key={g.grievance_id} className="rounded-2xl border border-stone-200 p-3" data-testid={`griev-${g.grievance_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-stone-900 truncate">{g.subject}</div>
                  <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[g.status] || STATUS_STYLE.submitted}`}>
                    {g.status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {g.category} · {g.city || "—"} · {new Date(g.created_at).toLocaleDateString()}
                </div>
                <div className="text-sm text-stone-700 mt-1 line-clamp-2">{g.body}</div>
                {(g.status_history || []).length > 1 && (
                  <details className="mt-2 text-xs text-stone-500">
                    <summary className="cursor-pointer">Status history ({g.status_history.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                      {g.status_history.map((h, i) => (
                        <li key={i}>· {h.status.replace("_", " ")} — {new Date(h.at).toLocaleString()} {h.note && `— ${h.note}`}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Schemes
 * ------------------------------------------------------------------------- */
function SchemesTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/gov/schemes", { params: { q: q || undefined, category: cat || undefined } })
       .then((r) => setItems(r.data));
  }, [q, cat]);

  const categories = ["Agriculture", "Housing", "Healthcare", "Girl Child", "Business", "Labour", "Retirement", "Banking", "Education", "Household"];

  return (
    <div data-testid="gov-schemes-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px] rounded-full border border-stone-200 pl-3">
            <SearchIcon size={14} className="text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search — 'housing', 'farmer', 'scholarship'"
              className="flex-1 py-2 text-sm bg-transparent outline-none"
              data-testid="schemes-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setCat("")} className={`text-xs font-bold px-3 py-1.5 rounded-full ${!cat ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`text-xs font-bold px-3 py-1.5 rounded-full ${cat === c ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`schemes-cat-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((s) => (
          <div key={s.slug} className="rounded-2xl bg-white border border-stone-200 p-4" data-testid={`scheme-${s.slug}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#2D5A27]">{s.category}</div>
              {s.benefit_inr > 0 && <div className="text-[10px] font-black text-emerald-700">Up to ₹{s.benefit_inr.toLocaleString("en-IN")}</div>}
            </div>
            <h3 className="heading text-base font-black text-stone-900 mt-1">{s.name}</h3>
            <p className="text-sm text-stone-600 mt-1">{s.summary}</p>
            <ul className="mt-2 text-xs text-stone-500 list-disc list-inside">
              {s.eligibility.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
            <a href={s.portal_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#E05E36] hover:underline" data-testid={`scheme-portal-${s.slug}`}>
              Open official portal <ExternalLink size={13} />
            </a>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-sm text-stone-500 py-10">No schemes match your filters.</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Certificates
 * ------------------------------------------------------------------------- */
function CertificatesTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "domicile", holder_name: "", father_or_husband_name: "", date_of_birth: "", address: "", purpose: "", aadhaar_last4: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/gov/certificates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (user) refresh(); }, [user]);

  const submit = async () => {
    if (!user) { toast.error("Sign in to request a certificate"); return; }
    if (!form.holder_name.trim()) { toast.error("Holder name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gov/certificates", form);
      toast.success("Certificate issued — download starting");
      window.open(`${API}/gov/certificates/${r.data.certificate_id}.pdf`, "_blank", "noopener,noreferrer");
      setForm({ ...form, holder_name: "", father_or_husband_name: "", date_of_birth: "", address: "", purpose: "", aadhaar_last4: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_1fr]" data-testid="gov-certificates-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <h2 className="heading text-lg font-black text-stone-900 mb-3">Request certificate</h2>
        <div className="space-y-2">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="cert-kind">
            {CERT_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label} Certificate</option>)}
          </select>
          <input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="Holder name" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="cert-holder" />
          <input value={form.father_or_husband_name} onChange={(e) => setForm({ ...form, father_or_husband_name: e.target.value })} placeholder="Father / Husband name (optional)" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="cert-guardian" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} placeholder="DOB (YYYY-MM-DD)" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none font-mono" data-testid="cert-dob" />
            <input value={form.aadhaar_last4} onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="Aadhaar last 4" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none font-mono" data-testid="cert-aadhaar" />
          </div>
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="cert-address" />
          <input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Purpose (optional)" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="cert-purpose" />
          <button
            onClick={submit}
            disabled={busy}
            className="mt-1 w-full bg-[#2D5A27] hover:bg-emerald-800 text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="cert-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Issue & Download
          </button>
        </div>
        <p className="text-[10px] text-stone-500 mt-3">
          This is a self-attested digital submission verifiable via find2near.com/verify. Not a substitute for a government-attested original.
        </p>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <h2 className="heading text-lg font-black text-stone-900 mb-3">Your certificates</h2>
        {items.length === 0 ? (
          <div className="text-sm text-stone-400 py-10 text-center">No certificates issued yet.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((c) => (
              <li key={c.certificate_id} className="flex items-center gap-3 rounded-xl border border-stone-200 p-3" data-testid={`cert-item-${c.certificate_id}`}>
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                  <FileText size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-stone-900 truncate">{c.kind_label}</div>
                  <div className="text-[11px] text-stone-500 truncate">{c.holder_name} · {new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                <a href={`${API}/gov/certificates/${c.certificate_id}.pdf`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 text-sm font-bold" data-testid={`cert-download-${c.certificate_id}`}>Download</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Public notices
 * ------------------------------------------------------------------------- */
function NoticesTab() {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [kind, setKind] = useState("");

  useEffect(() => {
    api.get("/gov/notices", { params: { city: city || undefined, kind: kind || undefined } })
       .then((r) => setItems(r.data));
  }, [city, kind]);

  const KINDS = ["announcement", "emergency", "health", "utility", "policy"];

  return (
    <div data-testid="gov-notices-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 mb-5 flex flex-wrap items-center gap-2">
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Filter by city" className="flex-1 min-w-[180px] px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="notices-city" />
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setKind("")} className={`text-xs font-bold px-3 py-1.5 rounded-full ${!kind ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700"}`}>All</button>
          {KINDS.map((k) => (
            <button key={k} onClick={() => setKind(k)} className={`text-xs font-bold px-3 py-1.5 rounded-full ${kind === k ? "bg-[#2D5A27] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>{k}</button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center text-sm text-stone-400 py-16 bg-white rounded-2xl border border-stone-200">No public notices yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((n) => (
            <li key={n.notice_id} className="rounded-2xl bg-white border border-stone-200 p-4" data-testid={`notice-${n.notice_id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">{n.kind}</div>
                <div className="text-[10px] text-stone-400">{new Date(n.created_at).toLocaleDateString()}</div>
              </div>
              <h3 className="heading text-lg font-black text-stone-900 mt-1">{n.title}</h3>
              <p className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{n.body}</p>
              <div className="text-[10px] text-stone-500 mt-2">— {n.author_name}{n.city ? ` · ${n.city}` : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
